/**
 * Where a player appears. One rule for everyone (first join, respawn without a
 * bed, agents): a random column within SPAWN_RADIUS of the world spawn point,
 * standing on the highest safe block of that column.
 *
 * "Safe" means solid ground that is not a tree, with two free cells of
 * headroom and no liquid: never a canopy, a trunk, a pond or a lava pool.
 */

import { BLOCKS, IS_LAVA, IS_SOLID, IS_WATERY } from '../blocks';
import { WORLD_HEIGHT } from '../constants';
import type { World } from '../world/chunk';

/** New players are scattered this far (blocks) around the spawn point, so they do not stack. */
export const SPAWN_RADIUS = 8;

let unsafe: Uint8Array | null = null; // 1 = pass through (leaves), 2 = never stand on
function unsafeTable(): Uint8Array {
  if (unsafe) return unsafe;
  unsafe = new Uint8Array(BLOCKS.length);
  for (const b of BLOCKS) {
    if (!b) continue;
    const n = b.name.toLowerCase();
    if (/leaves/.test(n)) unsafe[b.id] = 1;
    else if (/log|wood|cactus|magma|fire|berry|powder snow/.test(n)) unsafe[b.id] = 2;
  }
  return unsafe;
}

/** Feet y of the highest safe standing spot in column (x, z), or -1 when the column has none. */
export function safeSpawnY(world: World, x: number, z: number): number {
  const t = unsafeTable();
  for (let y = WORLD_HEIGHT - 3; y > 0; y--) {
    const id = world.getBlock(x, y, z);
    if (id === 0 || t[id] === 1) continue; // air and foliage: keep looking down
    if (IS_WATERY[id] || IS_LAVA[id]) return -1;
    if (!IS_SOLID[id]) continue; // grass, flowers, snow layers
    if (t[id] === 2) return -1;
    const a = world.getBlock(x, y + 1, z), b = world.getBlock(x, y + 2, z);
    if (IS_SOLID[a] || IS_SOLID[b] || IS_WATERY[a] || IS_LAVA[a] || IS_LAVA[b]) return -1;
    return y + 1;
  }
  return -1;
}

/**
 * A safe spot within `radius` of (cx, cz), or null when none of this round's
 * tries worked (columns whose chunk is not `ready` yet are skipped: call again
 * next frame).
 */
export function pickSpawn(
  world: World, cx: number, cz: number, radius: number,
  ready: (x: number, z: number) => boolean, rnd: () => number = Math.random, tries = 24,
): { x: number; y: number; z: number } | null {
  for (let i = 0; i < tries; i++) {
    const a = rnd() * Math.PI * 2, r = Math.sqrt(rnd()) * radius;
    const x = Math.floor(cx + Math.cos(a) * r), z = Math.floor(cz + Math.sin(a) * r);
    if (!ready(x, z)) continue;
    const y = safeSpawnY(world, x, z);
    if (y >= 0) return { x: x + 0.5, y, z: z + 0.5 };
  }
  return null;
}
